import { useMutation } from '@tanstack/react-query';
import type { CustomersRelationshipsRequestBody } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';

type Relationship = CustomersRelationshipsRequestBody['relationships'][number];

export async function createRelationships(workspaceId: number, relationships: Relationship[]): Promise<void> {
  await hydraUiApi.customers.createRelationships({
    params: { path: { environment_id: String(workspaceId) } },
    body: { relationships },
  });
}

export function useCreateRelationships(options: { mutationConfig?: HookMutationConfig<void, Relationship[]> } = {}) {
  const workspaceId = requireWorkspaceId();
  return useMutation({
    ...options.mutationConfig,
    mutationFn: (relationships: Relationship[]) => createRelationships(workspaceId, relationships),
  });
}

/**
 * Rewrite one relationship's attributes. The create endpoint is an upsert on
 * the entity pair, so this is the same POST with a single row: every kept
 * attribute at its value, and every removed one sent as an empty string,
 * which is how the API deletes a relationship attribute.
 *
 * The endpoint only accepts the person as `entity1` and the object as
 * `entity2`, so the pair is written in that order whichever way it was read.
 */
export async function updateRelationshipAttributes(
  workspaceId: number,
  relationship: Relationship,
  attributes: Readonly<Record<string, string>>,
  removedAttributes: readonly string[],
): Promise<void> {
  // A removed key that is also being written is a rename landing on it: the
  // kept value wins, since a blank would delete it.
  const removed = Object.fromEntries(
    removedAttributes.filter((key) => !Object.hasOwn(attributes, key)).map((key) => [key, '']),
  );
  const isPersonFirst = relationship.entity1_type === 'customer';
  await createRelationships(workspaceId, [
    {
      entity1_type: 'customer',
      entity1_id: isPersonFirst ? relationship.entity1_id : relationship.entity2_id,
      entity2_type: 'object',
      entity2_id: isPersonFirst ? relationship.entity2_id : relationship.entity1_id,
      created_at: relationship.created_at,
      attributes: { ...attributes, ...removed },
    },
  ]);
}

type UpdateRelationshipAttributesInput = {
  relationship: Relationship;
  attributes: Readonly<Record<string, string>>;
  removedAttributes: readonly string[];
};

export function useUpdateRelationshipAttributes(
  options: { mutationConfig?: HookMutationConfig<void, UpdateRelationshipAttributesInput> } = {},
) {
  const workspaceId = requireWorkspaceId();
  return useMutation({
    ...options.mutationConfig,
    mutationFn: ({ relationship, attributes, removedAttributes }: UpdateRelationshipAttributesInput) =>
      updateRelationshipAttributes(workspaceId, relationship, attributes, removedAttributes),
  });
}

export async function deleteRelationships(workspaceId: number, relationships: Relationship[]): Promise<void> {
  await hydraUiApi.customers.deleteRelationships({
    params: { path: { environment_id: String(workspaceId) } },
    // The endpoint matches a relationship on its entity pair, but still
    // length-validates any attributes it is given — so sending a row's own
    // attributes can make that row fail its own deletion. Send the pair only.
    body: { relationships: relationships.map((relationship) => ({ ...relationship, attributes: undefined })) },
  });
}

export function useDeleteRelationships(options: { mutationConfig?: HookMutationConfig<void, Relationship[]> } = {}) {
  const workspaceId = requireWorkspaceId();
  return useMutation({
    ...options.mutationConfig,
    mutationFn: (relationships: Relationship[]) => deleteRelationships(workspaceId, relationships),
  });
}
